/**
 * API Response Cache Utility
 * 使用 sessionStorage 快取 API 回應，避免重複請求
 */

import { logger } from './logger';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

// 預設 TTL（秒）
const DEFAULT_TTL = {
  // 設備狀態 - 快取時間短（即時性高）
  queryStatus: 30,      // 30 秒
  queryDevices: 60,     // 1 分鐘
  queryLocations: 300,  // 5 分鐘
  
  // 報告數據 - 快取時間中等
  generateReport_minute: 60,   // 1 分鐘（分粒度）
  generateReport_hour: 300,    // 5 分鐘（時粒度）
  generateReport_day: 600,     // 10 分鐘（日粒度）
};

const CACHE_PREFIX = 'ultron_api_cache_';

/**
 * 生成快取 key
 */
function generateCacheKey(command: string, params: any): string {
  // 對參數進行排序，確保相同參數生成相同的 key
  const sortedParams = JSON.stringify(params, Object.keys(params).sort());
  const hash = simpleHash(sortedParams);
  return `${CACHE_PREFIX}${command}_${hash}`;
}

/**
 * 簡單的字串雜湊函數
 */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * 根據 command 和參數決定 TTL
 */
function getTTL(command: string, params: any): number {
  if (command === 'QueryStatus') {
    return DEFAULT_TTL.queryStatus;
  }
  if (command === 'QueryDevices') {
    return DEFAULT_TTL.queryDevices;
  }
  if (command === 'QueryLocations') {
    return DEFAULT_TTL.queryLocations;
  }
  if (command === 'GenerateReport') {
    const granularity = params?.granularity || 'hour';
    if (granularity === 'minute') return DEFAULT_TTL.generateReport_minute;
    if (granularity === 'day') return DEFAULT_TTL.generateReport_day;
    return DEFAULT_TTL.generateReport_hour;
  }
  return 60; // 預設 1 分鐘
}

/**
 * 從快取讀取
 */
export function getFromCache<T>(command: string, params: any): T | null {
  try {
    const key = generateCacheKey(command, params);
    const cached = sessionStorage.getItem(key);
    
    if (!cached) return null;
    
    const entry: CacheEntry<T> = JSON.parse(cached);
    const now = Date.now();
    
    // 檢查是否過期
    if (now > entry.expiresAt) {
      sessionStorage.removeItem(key);
      logger.log(`🗑️ [Cache] Expired: ${command}`);
      return null;
    }
    
    const ageSeconds = Math.round((now - entry.timestamp) / 1000);
    logger.log(`✅ [Cache] Hit: ${command} (age: ${ageSeconds}s)`);
    return entry.data;
  } catch (error) {
    logger.warn('⚠️ [Cache] Read error:', error);
    return null;
  }
}

/**
 * 寫入快取
 */
export function setToCache<T>(command: string, params: any, data: T): void {
  try {
    const key = generateCacheKey(command, params);
    const ttl = getTTL(command, params);
    const now = Date.now();
    
    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      expiresAt: now + (ttl * 1000),
    };
    
    sessionStorage.setItem(key, JSON.stringify(entry));
    logger.log(`💾 [Cache] Saved: ${command} (TTL: ${ttl}s)`);
  } catch (error) {
    // sessionStorage 可能已滿，清理舊快取
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      clearExpiredCache();
      try {
        const key = generateCacheKey(command, params);
        const ttl = getTTL(command, params);
        const now = Date.now();
        const entry: CacheEntry<T> = {
          data,
          timestamp: now,
          expiresAt: now + (ttl * 1000),
        };
        sessionStorage.setItem(key, JSON.stringify(entry));
      } catch {
        logger.warn('⚠️ [Cache] Write failed after cleanup');
      }
    } else {
      logger.warn('⚠️ [Cache] Write error:', error);
    }
  }
}

/**
 * 清理過期快取
 */
export function clearExpiredCache(): void {
  const now = Date.now();
  let cleared = 0;
  
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (!key?.startsWith(CACHE_PREFIX)) continue;
    
    try {
      const cached = sessionStorage.getItem(key);
      if (!cached) continue;
      
      const entry: CacheEntry<any> = JSON.parse(cached);
      if (now > entry.expiresAt) {
        sessionStorage.removeItem(key);
        cleared++;
      }
    } catch {
      sessionStorage.removeItem(key!);
      cleared++;
    }
  }
  
  if (cleared > 0) {
    logger.log(`🗑️ [Cache] Cleared ${cleared} expired entries`);
  }
}

/**
 * 清除所有 API 快取
 */
export function clearAllCache(): void {
  let cleared = 0;
  
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX)) {
      sessionStorage.removeItem(key);
      cleared++;
    }
  }
  
  logger.log(`🗑️ [Cache] Cleared all ${cleared} cache entries`);
}

/**
 * 使快取失效（用於資料更新後）
 */
export function invalidateCache(command: string): void {
  let invalidated = 0;
  
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(`${CACHE_PREFIX}${command}_`)) {
      sessionStorage.removeItem(key);
      invalidated++;
    }
  }
  
  if (invalidated > 0) {
    logger.log(`🗑️ [Cache] Invalidated ${invalidated} entries for ${command}`);
  }
}

export default {
  get: getFromCache,
  set: setToCache,
  clearExpired: clearExpiredCache,
  clearAll: clearAllCache,
  invalidate: invalidateCache,
};

